import { useMemo } from 'react'
import { usePantryStore } from '../../../providers/PantryProvider'
import {
  useGeneralDashboardQuery,
  useSixDaysAgoQuery,
  useWebTrafficQuery,
} from './useDashboardQueries'
import type { MappedGeneralDashboard, MappedSixDaysAgo, MappedWebTraffic } from './useDashboardQueries'

export interface DashboardStateParams {
  startDate: string
  endDate: string
  /** Warehouse _id to filter by, or 'all' for all locations */
  locationId: string
  /** API source filter: '' = both, 'online' = online, 'pos' = in-store */
  source: string
  /** When true, revenue figures exclude taxes and discounts */
  netRevenue: boolean
}

// ---------------------------------------------------------------------------
// Derive real KPI values from API response
// locations_performance total row has location_id === "total_performance"
// ---------------------------------------------------------------------------

const deriveKpis = (
  data: MappedGeneralDashboard | undefined,
  webTraffic: MappedWebTraffic | undefined,
) => {
  const revenue = data?.revenue?.current ?? 0
  const orders = data?.numberOfOrders?.current ?? 0

  // Use locations_performance to get the online/in-store order split
  const total = data?.locationsPerformance?.find((l) => l.location_id === 'total_performance')
  const onlineOrders = total?.number_of_online_orders ?? Math.round(Number(orders) * 0.33)
  const posOrders = total?.number_of_pos_orders ?? Math.max(0, Number(orders) - onlineOrders)

  // Online / in-store revenue: prefer the clean read_general fields
  // (online_revenue / pos_revenue). Fall back to the order-count approximation
  // only when those fields are absent from the response.
  const round2 = (v: number) => Math.round(v * 100) / 100
  const onlineRevFraction = Number(orders) > 0 ? onlineOrders / Number(orders) : 0.33
  const onlineRev =
    data?.onlineRevenue != null ? data.onlineRevenue : round2(Number(revenue) * onlineRevFraction)
  const inStoreRev =
    data?.posRevenue != null ? data.posRevenue : Math.max(0, round2(Number(revenue) - onlineRev))

  const newCust = data?.customers?.new ?? 0
  const returningCust = data?.customers?.returning ?? 0
  const totalCustomers = newCust + returningCust
  const repeatRate = totalCustomers > 0 ? (returningCust / totalCustomers) * 100 : 0

  // Web traffic: sum sessions across all channel groups
  const totalSessions = (webTraffic ?? []).reduce((sum, row) => sum + Number(row.sessions || 0), 0)
  const totalPageviews = (webTraffic ?? []).reduce(
    (sum, row) => sum + Number(row.pageviewsPerSession || 0) * Number(row.sessions || 0),
    0,
  )

  // Trend percentages (percentage_increase from KpiMetric; null → 0)
  const toTrend = (v: number | null | undefined) => Number(v ?? 0)

  return {
    totalRev: Number(revenue),
    onlineRev,
    inStoreRev,
    orders: Number(orders),
    onlineOrders,
    inStoreOrders: posOrders,
    aov: data?.averageOrderAmount?.current ?? 0,
    // Conversion rate — real read_general field (already a percent 0..100, no ×100)
    convRate: data?.conversionRate?.current ?? 0,
    customers: totalCustomers,
    newCust,
    repeatRate,
    ltv: data?.profit?.current ?? 0,
    // Delivery KPIs — not in this API, default to zero until a delivery API is added
    deliveries: 0,
    completedDel: 0,
    avgDelTime: 0,
    onTimeRate: 0,
    cancelled: 0,
    pending: 0,
    avgItems: data?.averageCartSize?.current ?? 0,
    sessions: totalSessions,
    // Total sessions count from read_general — distinct from the GA4 `sessions` feed above
    totalSessions: data?.totalSessions?.current ?? 0,
    impressions: totalSessions > 0 ? Math.round(totalPageviews / totalSessions) : 0,
    // Trend percentages (vs previous period from the API)
    trends: {
      totalRev: toTrend(data?.revenue?.percentage_increase),
      // Per-channel trend from online_revenue / pos_revenue when present,
      // else fall back to the overall revenue trend.
      onlineRev: toTrend(data?.onlineRevenueTrend ?? data?.revenue?.percentage_increase),
      inStoreRev: toTrend(data?.posRevenueTrend ?? data?.revenue?.percentage_increase),
      orders: toTrend(data?.numberOfOrders?.percentage_increase),
      aov: toTrend(data?.averageOrderAmount?.percentage_increase),
      convRate: toTrend(data?.conversionRate?.percentage_increase),
      ltv: toTrend(data?.profit?.percentage_increase),
      customers: 0,  // no trend from API for customer counts
      newCust: 0,
      repeatRate: 0,
    },
  }
}

export type DashboardKpis = ReturnType<typeof deriveKpis>

// ---------------------------------------------------------------------------
// Map top_products to dashboard display shape
// Real fields: name, revenue, profit, stock_quantity, stock_status, sold_quantity
// No category/brand in API — left as empty string
// ---------------------------------------------------------------------------

const mapTopProducts = (data: MappedGeneralDashboard | undefined) =>
  (data?.topProducts ?? []).slice(0, 8).map((p) => ({
    id: String(p.id),
    name: p.name,
    image: p.image?.src ?? '',
    cat: '',
    brand: '',
    rev: p.revenue,
    orders: Math.round(p.sold_quantity),
    margin: p.revenue > 0 ? Math.round((p.avg_profit / p.revenue) * 100) : 0,
    stock: p.stock_quantity,
    status: p.stock_status === 'instock' ? 'in-stock' : p.stock_status,
    trend: 0,
  }))

export type DashboardTopProduct = ReturnType<typeof mapTopProducts>[number]

const mapTopCategories = (data: MappedGeneralDashboard | undefined) =>
  (data?.topCategories ?? []).slice(0, 6).map((c) => ({
    id: String(c.id),
    cat: c.name,
    rev: c.revenue,
  }))

// ---------------------------------------------------------------------------
// Main state hook — consumed by DashboardProvider
// ---------------------------------------------------------------------------

export const useDashboardState = ({ startDate, endDate, locationId, source, netRevenue }: DashboardStateParams) => {
  const store = usePantryStore()
  const storeId = store?.storeId ?? ''
  // syncCompleted is undefined before store loads; treat undefined as synced
  const isSyncedStore = store?.syncCompleted !== false

  // Only pass locationId to the API when a specific location is selected
  const apiLocationId = locationId !== 'all' ? locationId : null

  const {
    data,
    isFetching: isGeneralFetching,
    isError,
    refetch,
  } = useGeneralDashboardQuery({
    startDate,
    endDate,
    locationId: apiLocationId,
    source,
    netRevenue,
    enabled: isSyncedStore,
  })

  const { data: sixDaysAgoData } = useSixDaysAgoQuery({
    locationId: apiLocationId,
    source,
    netRevenue,
    storeId,
    enabled: isSyncedStore,
  })

  const { data: webTrafficData } = useWebTrafficQuery({
    startDate: `${startDate}T00:00:00`,
    endDate: `${endDate}T23:59:59`,
    enabled: isSyncedStore,
  })

  const isLoading = isGeneralFetching

  const liveKpis = useMemo(() => deriveKpis(data, webTrafficData), [data, webTrafficData])
  const topProducts = useMemo(() => mapTopProducts(data), [data])
  const topCategories = useMemo(() => mapTopCategories(data), [data])
  const locationsPerformance = useMemo(() => data?.locationsPerformance ?? [], [data])
  const locationsRevenue = useMemo(() => data?.locationsRevenue ?? [], [data])
  const shippingRevenue = useMemo(() => data?.shippingRevenue ?? [], [data])
  const revenueByDay = useMemo(() => data?.revenueByDay ?? [], [data])
  const ordersByDay = useMemo(() => data?.ordersByDay ?? [], [data])

  return {
    storeId,
    startDate,
    endDate,
    isLoading,
    isError,
    refetch,
    liveKpis,
    topProducts,
    topCategories,
    locationsPerformance,
    locationsRevenue,
    shippingRevenue,
    revenueByDay,
    ordersByDay,
    sixDaysAgoData,
    webTrafficData,
  }
}

export type DashboardState = ReturnType<typeof useDashboardState>
